import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { BottomNavBar } from '../../components/BottomNavBar';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import { TaskItem } from '../../components/TaskItem';
import { RootStackParamList } from '../../types/navigation';
import { PASSWORDS_TASKS_CONFIG, PERSONAL_TASKS_CONFIG, MEDICAL_TASKS_CONFIG, CONTACTS_TASKS_CONFIG, EXPENSES_TASKS_CONFIG, FINANCE_TASKS_CONFIG, PROPERTY_TASKS_CONFIG, FINAL_WISHES_TASKS_CONFIG, HEALTHCARE_DIRECTIVES_TASKS_CONFIG, ESTATE_PLANNING_TASKS_CONFIG, CARE_PREFERENCES_TASKS_CONFIG, LOYALTY_POINTS_TASKS_CONFIG } from '../../constants/taskIds';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { expenseService } from '../../services/expenseService';
import { documentStorageService } from '../../services/documentStorageService';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('OnlineAccountsChecklist');

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Task {
  id: string;
  title: string;
  category?: string;
}

interface CategoryCardProps {
  item: {
    id: string;
    name: string;
    icon: any;
    count: number;
  };
  isSelected: boolean;
  navigation: NavigationProp;
  onPress: (id: string) => void;
}

const ONLINE_ACCOUNTS_TASKS = PASSWORDS_TASKS_CONFIG.map(task => ({
  id: task.id.toString(),
  title: task.title,
  category: task.category,
}));

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, navigation, onPress }) => {
  const handleCardPress = () => {
    onPress(item.id);
    switch (item.id) {
      case 'all':
        navigation.navigate('Dashboard', { screen: 'AllTasks' });
        break;
      case 'personal':
        navigation.navigate('Dashboard', { screen: 'PersonalChecklist' });
        break;
      case 'medical':
        navigation.navigate('Dashboard', { screen: 'MedicalChecklist' });
        break;
      case 'finance':
        navigation.navigate('Dashboard', { screen: 'FinanceChecklist' });
        break;
      case 'property':
        navigation.navigate('Dashboard', { screen: 'PropertyChecklist' });
        break;
      case 'finalWishes':
        navigation.navigate('Dashboard', { screen: 'FinalWishesChecklist' });
        break;
      case 'healthcareDirectives':
        navigation.navigate('Dashboard', { screen: 'HealthcareDirectivesChecklist' });
        break;
      case 'estatePlanning':
        navigation.navigate('Dashboard', { screen: 'EstatePlanningChecklist' });
        break;
      case 'carePreferences':
        navigation.navigate('Dashboard', { screen: 'CarePreferencesChecklist' });
        break;
      case 'passwords':
        navigation.navigate('Dashboard', { screen: 'OnlineAccountsChecklist' });
        break;
      case 'contacts':
        navigation.navigate('Dashboard', { screen: 'EmergencyContactsChecklist' });
        break;
      case 'expenses':
        navigation.navigate('Dashboard', { screen: 'ExpensesChecklist' });
        break;
      case 'documents':
        navigation.navigate('Dashboard', { screen: 'DocumentManagementChecklist' });
        break;
      case 'loyaltyPoints':
        navigation.navigate('Dashboard', { screen: 'LoyaltyPointsChecklist' });
        break;
    }
  };

  return (
    <TouchableOpacity 
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#29BFC0' : '#FFFFFF' }
      ]}
      onPress={handleCardPress}
    >
      <View style={styles.categoryIconContainer}>
        <Image source={item.icon} style={styles.categoryIcon} />
      </View>
      <Text style={[
        styles.categoryName,
        { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
      ]}>{item.name}</Text>
      <Text style={[
        styles.categoryCount,
        { color: isSelected ? '#FFFFFF' : '#6B7280' }
      ]}>{item.count} Tasks</Text>
      <TouchableOpacity 
        style={[
          styles.addButton,
          { backgroundColor: isSelected ? '#FFFFFF' : '#FF9F5A' }
        ]}
        onPress={handleCardPress}
      >
        <Image 
          source={require('../../assets/icons/add.png')}
          style={[
            styles.addIcon,
            { tintColor: isSelected ? '#29BFC0' : '#FFFFFF' }
          ]} 
        />
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

export const OnlineAccountsChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData } = useSubscription();
  const [selectedCategory, setSelectedCategory] = useState('passwords');
  const [completedTaskIds, setCompletedTaskIds] = useState<string[]>([]);
  const [categoryProgress, setCategoryProgress] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [passwordCount, setPasswordCount] = useState(0);
  const [categoryPasswordCounts, setCategoryPasswordCounts] = useState<Record<string, number>>({});

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Passwords require death verification when viewing another user's data
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedPersonalTasks, setCompletedPersonalTasks] = useState<string[]>([]);
  const [completedMedicalTasks, setCompletedMedicalTasks] = useState<string[]>([]);
  const [completedFinanceTasks, setCompletedFinanceTasks] = useState<string[]>([]);
  const [completedPropertyTasks, setCompletedPropertyTasks] = useState<string[]>([]);
  const [completedFinalWishesTasks, setCompletedFinalWishesTasks] = useState<string[]>([]);
  const [completedHealthcareDirectivesTasks, setCompletedHealthcareDirectivesTasks] = useState<string[]>([]);
  const [completedEstatePlanningTasks, setCompletedEstatePlanningTasks] = useState<string[]>([]);
  const [completedCarePreferencesTasks, setCompletedCarePreferencesTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);

  useEffect(() => {
    fetchCompletedTasks();
    fetchCategoryProgress();
  }, []);

  // Refresh completion status when password counts change
  useEffect(() => {
    fetchCompletedTasks();
  }, [passwordCount]);

  const fetchCompletedTasks = async () => {
    try {
      // For password tasks, determine completion based on actual password data
      // (K5: the owner's list when viewing another person after a verified
      // death; nothing before that).
      const allPasswords = requiresDeathVerification
        ? []
        : await vaultService.getAllPasswords(isViewingOthersData ? selectedUserId : undefined, { quiet: true });
      const completedPasswordTasks: string[] = [];
      
      ONLINE_ACCOUNTS_TASKS.forEach(task => {
        if (task.category) {
          const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category);
          if (categoryPasswords.length > 0) {
            completedPasswordTasks.push(task.id);
          }
        }
      });
      
      setCompletedTaskIds(completedPasswordTasks);
    } catch (error) {
      logger.error('Error fetching completed password tasks:', error);
      setCompletedTaskIds([]);
    }
  };

  const fetchCategoryProgress = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/tasks/category-progress`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategoryProgress(data);
      } else {
        setCategoryProgress([]);
      }
    } catch (e) {
      setCategoryProgress([]);
    }
  };

  // Map backend progress to categories for ChecklistCardScroll
  const categories = [
    { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png') },
    { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png') },
    { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png') },
    { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png') },
    { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png') },
    { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png') },
    { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png') },
    { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png') },
    { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png') },
    { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png') },
    { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png') },
    { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png') },
    { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png') },
    { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png') },
  ].map(cat => {
    if (cat.id === 'all') {
      // For all category, calculate total completion across all categories
      const totalAllTasks = PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length +
                           PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length + HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length +
                           ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length +
                           PASSWORDS_TASKS_CONFIG.length + CONTACTS_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length +
                           LOYALTY_POINTS_TASKS_CONFIG.length + 8; // 8 document categories
      const completedAllTasks = completedPersonalTasks.length + completedMedicalTasks.length + completedFinanceTasks.length +
                               completedPropertyTasks.length + completedFinalWishesTasks.length + completedHealthcareDirectivesTasks.length +
                               completedEstatePlanningTasks.length + completedCarePreferencesTasks.length +
                               completedTaskIds.length + completedContactTasks.length + completedExpenseTasks.length +
                               completedLoyaltyPointTasks.length + completedDocumentTasks.length;
      return { ...cat, count: completedAllTasks, totalTasks: totalAllTasks };
    } else if (cat.id === 'personal') {
      // For personal category, use personal info data to calculate progress
      const completedPersonalTasksCount = completedPersonalTasks.length;
      const totalPersonalTasks = PERSONAL_TASKS_CONFIG.length;
      return { ...cat, count: completedPersonalTasksCount, totalTasks: totalPersonalTasks };
    } else if (cat.id === 'medical') {
      // For medical category, use medical data to calculate progress
      const completedMedicalTasksCount = completedMedicalTasks.length;
      const totalMedicalTasks = MEDICAL_TASKS_CONFIG.length;
      return { ...cat, count: completedMedicalTasksCount, totalTasks: totalMedicalTasks };
    } else if (cat.id === 'finance') {
      // For finance category, use finance data to calculate progress
      const completedFinanceTasksCount = completedFinanceTasks.length;
      const totalFinanceTasks = FINANCE_TASKS_CONFIG.length;
      return { ...cat, count: completedFinanceTasksCount, totalTasks: totalFinanceTasks };
    } else if (cat.id === 'property') {
      // For property category, use property data to calculate progress
      const completedPropertyTasksCount = completedPropertyTasks.length;
      const totalPropertyTasks = PROPERTY_TASKS_CONFIG.length;
      return { ...cat, count: completedPropertyTasksCount, totalTasks: totalPropertyTasks };
    } else if (cat.id === 'finalWishes') {
      // For final wishes category, use final wishes data to calculate progress
      const completedFinalWishesTasksCount = completedFinalWishesTasks.length;
      const totalFinalWishesTasks = FINAL_WISHES_TASKS_CONFIG.length;
      return { ...cat, count: completedFinalWishesTasksCount, totalTasks: totalFinalWishesTasks };
    } else if (cat.id === 'healthcareDirectives') {
      // For healthcare directives category, use healthcare directives data to calculate progress
      const completedHealthcareDirectivesTasksCount = completedHealthcareDirectivesTasks.length;
      const totalHealthcareDirectivesTasks = HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length;
      return { ...cat, count: completedHealthcareDirectivesTasksCount, totalTasks: totalHealthcareDirectivesTasks };
    } else if (cat.id === 'estatePlanning') {
      // For estate planning category, use estate planning data to calculate progress
      const completedEstatePlanningTasksCount = completedEstatePlanningTasks.length;
      const totalEstatePlanningTasks = ESTATE_PLANNING_TASKS_CONFIG.length;
      return { ...cat, count: completedEstatePlanningTasksCount, totalTasks: totalEstatePlanningTasks };
    } else if (cat.id === 'carePreferences') {
      // For care preferences category, use care preferences data to calculate progress
      const completedCarePreferencesTasksCount = completedCarePreferencesTasks.length;
      const totalCarePreferencesTasks = CARE_PREFERENCES_TASKS_CONFIG.length;
      return { ...cat, count: completedCarePreferencesTasksCount, totalTasks: totalCarePreferencesTasks };
    } else if (cat.id === 'passwords') {
      // For passwords category, use password data to calculate progress
      const completedPasswordTasks = completedTaskIds.length;
      const totalPasswordTasks = ONLINE_ACCOUNTS_TASKS.length;
      return { ...cat, count: completedPasswordTasks, totalTasks: totalPasswordTasks };
    } else if (cat.id === 'contacts') {
      // For contacts category, use contact data to calculate progress
      const completedContactTasksCount = completedContactTasks.length;
      const totalContactTasks = CONTACTS_TASKS_CONFIG.length;
      return { ...cat, count: completedContactTasksCount, totalTasks: totalContactTasks };
    } else if (cat.id === 'expenses') {
      // For expenses category, use expense data to calculate progress
      const completedExpenseTasksCount = completedExpenseTasks.length;
      const totalExpenseTasks = EXPENSES_TASKS_CONFIG.length;
      return { ...cat, count: completedExpenseTasksCount, totalTasks: totalExpenseTasks };
    } else if (cat.id === 'loyaltyPoints') {
      // For loyalty points category, use loyalty point data to calculate progress
      const completedLoyaltyPointTasksCount = completedLoyaltyPointTasks.length;
      const totalLoyaltyPointTasks = LOYALTY_POINTS_TASKS_CONFIG.length;
      return { ...cat, count: completedLoyaltyPointTasksCount, totalTasks: totalLoyaltyPointTasks };
    } else if (cat.id === 'documents') {
      // For documents category, use document data to calculate progress
      const completedDocumentTasksCount = completedDocumentTasks.length;
      const totalDocumentTasks = 8; // 8 document categories: personal, legal, medical, financial, property, insurance, digital, other
      return { ...cat, count: completedDocumentTasksCount, totalTasks: totalDocumentTasks };
    } else {
      // For other categories, use backend progress
      const progress = categoryProgress.find((p: any) => p.categoryId === cat.id) || { completedCount: 0, totalCount: 0 };
      return { ...cat, count: progress.completedCount, totalTasks: progress.totalCount };
    }
  });

  const handleCategoryPress = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  // Fetch password counts for each category
  const fetchPasswordCounts = async () => {
    try {
      const allPasswords = await vaultService.getAllPasswords();
      setPasswordCount(allPasswords.length);
      
      const counts: Record<string, number> = {};
      ONLINE_ACCOUNTS_TASKS.forEach(task => {
        if (task.category) {
          counts[task.category] = allPasswords.filter(p => p.category.toLowerCase() === task.category).length;
        }
      });
      setCategoryPasswordCounts(counts);
    } catch (error) {
      logger.error('Error fetching password counts:', error);
    }
  };

  // Fetch contact completion status
  const fetchContactCompletion = async () => {
    try {
      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        logger.debug('Contact completion fetch timeout - using fallback');
        setCompletedContactTasks([]);
      }, 5000); // 5 second timeout

      // For contact tasks, determine completion based on actual contact data
      const allContacts = await contactService.getAllContacts();
      clearTimeout(timeoutId);

      const completedContactTasks: string[] = [];

      CONTACTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category.toLowerCase());
          if (categoryContacts.length > 0) {
            completedContactTasks.push(task.id.toString());
          }
        }
      });

      setCompletedContactTasks(completedContactTasks);
    } catch (error) {
      logger.error('Error fetching completed contact tasks (using fallback):', error);
      setCompletedContactTasks([]);
    }
  };

  const fetchExpenseCompletion = async () => {
    try {
      const allExpenses = await expenseService.getAllExpenses();
      const completedExpenseTasks: string[] = [];

      EXPENSES_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
          if (categoryExpenses.length > 0) {
            completedExpenseTasks.push(task.id.toString());
          }
        }
      });

      setCompletedExpenseTasks(completedExpenseTasks);
    } catch (error) {
      logger.error('Error fetching completed expense tasks:', error);
      setCompletedExpenseTasks([]);
    }
  };

  const fetchCompletedTasksFromBackend = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const res = await fetch(`${API_URL}/api/tasks/user/completed-tasks`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));

        // Calculate completed tasks for each category
        const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
        const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
        const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
        const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
        const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
        const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
        const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
        const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

        setCompletedPersonalTasks(completedTaskIds.filter((id: number) => personalIds.has(id)).map((id: number) => id.toString()));
        setCompletedMedicalTasks(completedTaskIds.filter((id: number) => medicalIds.has(id)).map((id: number) => id.toString()));
        setCompletedFinanceTasks(completedTaskIds.filter((id: number) => financeIds.has(id)).map((id: number) => id.toString()));
        setCompletedPropertyTasks(completedTaskIds.filter((id: number) => propertyIds.has(id)).map((id: number) => id.toString()));
        setCompletedFinalWishesTasks(completedTaskIds.filter((id: number) => finalWishesIds.has(id)).map((id: number) => id.toString()));
        setCompletedHealthcareDirectivesTasks(completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).map((id: number) => id.toString()));
        setCompletedEstatePlanningTasks(completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).map((id: number) => id.toString()));
        setCompletedCarePreferencesTasks(completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).map((id: number) => id.toString()));
      }
    } catch (error) {
      logger.error('Error fetching completed tasks from backend:', error);
    }
  };

  useEffect(() => {
    fetchPasswordCounts();
  }, []);

  // Refresh password counts when screen comes into focus
  const fetchLoyaltyPointCompletion = async () => {
    try {
      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        logger.debug('Loyalty point completion fetch timeout - using fallback');
        setCompletedLoyaltyPointTasks([]);
      }, 5000); // 5 second timeout

      // For loyalty point tasks, determine completion based on actual loyalty point data
      const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints();
      clearTimeout(timeoutId);

      const completedLoyaltyPointTasks: string[] = [];

      LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
          if (categoryLoyaltyPoints.length > 0) {
            completedLoyaltyPointTasks.push(task.id.toString());
          }
        }
      });

      setCompletedLoyaltyPointTasks(completedLoyaltyPointTasks);
    } catch (error) {
      logger.error('Error fetching completed loyalty point tasks (using fallback):', error);
      setCompletedLoyaltyPointTasks([]);
    }
  };

  const fetchDocumentCompletion = async () => {
    try {
      const allDocuments = await documentStorageService.listDocuments();
      const completedDocumentTasksList: string[] = [];

      const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
      const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];

      DOCUMENT_CATEGORIES.forEach((category, index) => {
        const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
        if (categoryDocuments.length > 0) {
          completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
        }
      });

      setCompletedDocumentTasks(completedDocumentTasksList);
    } catch (error) {
      logger.error('Error fetching completed document tasks:', error);
      setCompletedDocumentTasks([]);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchPasswordCounts();
      fetchCompletedTasks();
      fetchContactCompletion();
      fetchLoyaltyPointCompletion();
      fetchExpenseCompletion();
      fetchDocumentCompletion();
      fetchCompletedTasksFromBackend();
    }, [])
  );

  // Recalculate categories when completion status changes
  useEffect(() => {
    // This will trigger a re-render of the categories with updated completion data
  }, [completedTaskIds, completedPersonalTasks, completedMedicalTasks, completedFinanceTasks, completedPropertyTasks, completedFinalWishesTasks, completedHealthcareDirectivesTasks, completedEstatePlanningTasks, completedCarePreferencesTasks, completedContactTasks, completedExpenseTasks, completedLoyaltyPointTasks, completedDocumentTasks, categoryProgress]);

  // Determine if tasks should be disabled
  const isTaskDisabled = !canViewOwnData && !isViewingOthersData;

  const handleTaskPress = async (taskId: string) => {
    const task = ONLINE_ACCOUNTS_TASKS.find(t => t.id === taskId);
    if (!task) return;

    try {
      // Navigation logic - go to specific category on password dashboard
      if (task.category) {
        navigation.navigate('Passwords' as never, {
          screen: 'PasswordsDashboardScreen',
          params: { selectedCategory: task.category }
        } as never);
      }
    } catch (error) {
      logger.error('Error handling task press:', error);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.headerPlaceholder} />
        </View>
        <ChecklistCardScroll
          categories={categories}
          selectedCategory={selectedCategory}
          navigation={navigation}
          onCategoryPress={handleCategoryPress}
        />
        <Text style={styles.title}>Online Accounts Checklist</Text>

        {/* Death Verification Required Banner for successors */}
        {requiresDeathVerification && (
          <View style={styles.deathVerificationBanner}>
            <Text style={styles.deathVerificationTitle}>Access Restricted</Text>
            <Text style={styles.deathVerificationText}>
              Password information for {viewingUserName} will only be accessible to Successors after an end of life event has been verified.
            </Text>
            <Text style={styles.deathVerificationSubtext}>
              To gain access, a Successor must submit a death certificate through the app and complete the verification process. If you are a Keyholder, your access is restricted.
            </Text>
          </View>
        )}

        {/* Show content only if not requiring death verification */}
        {!requiresDeathVerification && (
          <>
            {/* View All Passwords Button - appears after first password */}
            {passwordCount > 0 && (
              <TouchableOpacity
                style={styles.viewAllButton}
                onPress={() => navigation.navigate('Passwords' as never, { screen: 'PasswordsDashboardScreen' } as never)}
              >
                <Text style={styles.viewAllButtonText}>View All Passwords</Text>
              </TouchableOpacity>
            )}

            {error && (
              <View style={{ padding: 20 }}>
                <Text style={{ color: 'red', fontWeight: 'bold' }}>{error}</Text>
              </View>
            )}
            {!isViewingOthersData && <UpgradeBanner variant="compact" />}

            <ScrollView style={styles.contentContainer} contentContainerStyle={{ paddingBottom: 200 }}>
              {ONLINE_ACCOUNTS_TASKS.map((task) => {
                const categoryCount = categoryPasswordCounts[task.category || ''] || 0;
                const hasPasswords = categoryCount > 0;

                return (
                  <TaskItem
                    key={task.id}
                    task={task}
                    isCompleted={completedTaskIds.includes(task.id)}
                    onPress={() => handleTaskPress(task.id)}
                    onCheckboxPress={() => {}}
                    buttonText={hasPasswords ? 'Manage Passwords' : 'Get Started'}
                    buttonColor={hasPasswords ? '#FF9F5A' : '#29BFC0'}
                    isDisabled={isTaskDisabled}
                  />
                );
              })}
            </ScrollView>
          </>
        )}
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
    marginLeft: ms(30),
  },
  viewAllButton: {
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(20),
    paddingVertical: ms(12),
    borderRadius: ms(8),
    marginHorizontal: ms(20),
    marginBottom: ms(20),
    alignItems: 'center',
  },
  viewAllButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
    marginHorizontal: ms(15),
    marginVertical: ms(20),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  taskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    borderRadius: ms(8),
    marginRight: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#29BFC0',
  },
  taskInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  taskTitle: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
  },
  completeButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
  },
  completeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '500',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: 0,
    height: undefined,
    borderWidth: 0,
  },
  categoryCard: {
    width: s(130),
    height: s(160),
    borderRadius: ms(15),
    padding: ms(12),
    marginRight: ms(15),
    marginVertical: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  categoryCount: {
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
  addButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    width: s(15),
    height: s(15),
  },
  deathVerificationBanner: {
    backgroundColor: '#FEF3C7',
    borderWidth: ms(1),
    borderColor: '#F59E0B',
    borderRadius: ms(12),
    padding: ms(20),
    marginHorizontal: ms(20),
    marginVertical: ms(20),
  },
  deathVerificationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  deathVerificationText: {
    fontSize: ms(14, 0.3),
    color: '#92400E',
    textAlign: 'center',
    marginBottom: ms(10),
    lineHeight: ms(20, 0.3),
  },
  deathVerificationSubtext: {
    fontSize: ms(12, 0.3),
    color: '#B45309',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
}); 